// packages/frontend/src/components/admin/SportSeasonsMatrix.tsx
import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useSportSeasonsStore } from '../../store/sportSeasonsStore';
import type { SportSeasonDefault, Gender, Season, Confidence } from '../../types/sportSeason';
import { SportSeasonCellModal } from './SportSeasonCellModal';
import { genderLabel } from '../../lib/sportLabel';

const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
  'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
  'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// Light/dark pill classes per spec §2. Inline because there is one consumer.
const SEASON_PILL_CLASS: Record<Season, string> = {
  FALL: 'bg-orange-100 text-orange-900 dark:bg-orange-500/20 dark:text-orange-300',
  WINTER: 'bg-blue-100 text-blue-900 dark:bg-blue-500/20 dark:text-blue-300',
  SPRING: 'bg-green-100 text-green-900 dark:bg-green-500/20 dark:text-green-300',
  SUMMER: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-500/20 dark:text-yellow-300',
};

const SEASON_LABEL: Record<Season, string> = {
  FALL: 'Fall',
  WINTER: 'Win',
  SPRING: 'Spr',
  SUMMER: 'Sum',
};

function SeasonPill({ season }: { season: Season }) {
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[44px] h-[22px] rounded text-xs font-semibold ${SEASON_PILL_CLASS[season]}`}
    >
      {SEASON_LABEL[season]}
    </span>
  );
}

function ConfidenceDot({ confidence }: { confidence: Confidence }) {
  const dotClass = confidence === 'MEDIUM' ? 'bg-amber-400' : 'bg-orange-500';
  return (
    <span
      className={`ml-1 inline-block w-1.5 h-1.5 rounded-full ${dotClass}`}
      aria-label={`Confidence: ${confidence}`}
    />
  );
}

function RegionalCountBadge({ count }: { count: number }) {
  return (
    <span
      className="ml-1 inline-flex items-center text-[10px] px-1 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"
      title={`${count} regional override${count === 1 ? '' : 's'} — open to view`}
    >
      +{count}
    </span>
  );
}

export function SportSeasonsMatrix() {
  const { matrix, coverage, loading, loadMatrix } = useSportSeasonsStore();
  const [selected, setSelected] = useState<SportSeasonDefault | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('ALL');

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  // Filter sport rows by case-insensitive substring on sportName.
  const filteredSports = useMemo(() => {
    const sports = Object.keys(matrix).sort();
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return sports;
    return sports.filter((s) => s.toLowerCase().includes(needle));
  }, [matrix, searchTerm]);

  // Restrict visible state columns to the picked state (or all).
  const visibleStates = useMemo(() => {
    if (stateFilter === 'ALL') return STATES;
    return STATES.filter((s) => s === stateFilter);
  }, [stateFilter]);

  if (loading && Object.keys(matrix).length === 0) {
    return <div className="text-sm text-gray-500 dark:text-gray-400 p-4">Loading…</div>;
  }

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
          />
          <input
            type="text"
            placeholder="Search sport…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-7 w-56"
          />
        </div>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="input w-36"
        >
          <option value="ALL">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {coverage && (
          <div className="ml-auto text-xs text-gray-500 dark:text-gray-400">
            {coverage.totals.total} cells · HIGH {coverage.byConfidence.HIGH} · MED{' '}
            {coverage.byConfidence.MEDIUM} · LOW {coverage.byConfidence.LOW}
          </div>
        )}
      </div>

      {/* Matrix */}
      <div className="card overflow-auto max-h-[calc(100vh-260px)]">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-30 bg-gray-50 dark:bg-gray-700/50 text-left px-3 py-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                Sport / Gender
              </th>
              {visibleStates.map((s) => (
                <th
                  key={s}
                  className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-700/50 px-2 py-2 font-mono text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700"
                >
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSports.length === 0 ? (
              <tr>
                <td
                  colSpan={1 + visibleStates.length}
                  className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic"
                >
                  No sports match "{searchTerm}"
                </td>
              </tr>
            ) : (
              filteredSports.flatMap((sport) =>
                (Object.keys(matrix[sport]) as Gender[]).sort().map((gender) => {
                  const rowsByState = new Map(
                    matrix[sport][gender].map((r) => [r.stateCode, r]),
                  );
                  return (
                    <tr
                      key={`${sport}-${gender}`}
                      className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-3 py-2 whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">
                        {sport}
                        {genderLabel(gender) && (
                          <>
                            {' '}
                            <span className="text-gray-500 dark:text-gray-400 font-normal">
                              ({genderLabel(gender)})
                            </span>
                          </>
                        )}
                      </td>
                      {visibleStates.map((st) => {
                        const cell = rowsByState.get(st);
                        if (!cell) {
                          return (
                            <td
                              key={st}
                              className="px-2 py-1 text-center text-gray-300 dark:text-gray-600"
                            >
                              —
                            </td>
                          );
                        }
                        if (!cell.sanctioned) {
                          return (
                            <td
                              key={st}
                              className="px-2 py-1 text-center text-gray-400 dark:text-gray-500"
                              title="Not sanctioned"
                            >
                              ·
                            </td>
                          );
                        }
                        return (
                          <td key={st} className="px-1 py-1">
                            <button
                              type="button"
                              onClick={() => setSelected(cell)}
                              className="w-full px-1 py-1 rounded hover:ring-2 hover:ring-blast-500 dark:hover:ring-blast-400 ring-offset-1 ring-offset-white dark:ring-offset-gray-800 transition-shadow inline-flex items-center justify-center"
                              title={`${cell.confidence}${cell.notes ? ` — ${cell.notes}` : ''}`}
                            >
                              {cell.primarySeason ? (
                                <SeasonPill season={cell.primarySeason} />
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">?</span>
                              )}
                              {cell.confidence !== 'HIGH' && (
                                <ConfidenceDot confidence={cell.confidence} />
                              )}
                              {(cell.regionalCount ?? 0) > 0 && (
                                <RegionalCountBadge count={cell.regionalCount ?? 0} />
                              )}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                }),
              )
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <SportSeasonCellModal cell={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
